// src/PdfExportModal.jsx
import React, { useEffect, useState } from "react";
import {hasPageOverflow} from '../helpers/pageLayout';
import {capturePage,snapshotPages} from "../helpers/capturePage";
import jsPDF from "jspdf";
import "./PdfExportModal.css"
import { useAuth } from "../../Login/AuthContext";
import { deductCredits } from "../creditsDeduct";
import dollar from '../../../assets/hamburgermenu_icons/dollar.svg'



export default function PdfExportModal({ isOpen, onClose, editorRootRef }) {
  const [loading, setLoading] = useState(false);
  const [pages, setPages] = useState([]); // { index, dataUrl, width, height }
  const [selected, setSelected] = useState([]);
  const [error, setError] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);

  const { user, setWorksheetsCount, isAuthenticated , worksheetsCount } = useAuth();



  useEffect(() => {
    if (!isOpen) return;
    if (!editorRootRef.current) return;

    async function generatePreviews() {
      setLoading(true);
      setError(null);
      setPages([]);
      setSelected([]);

      try {
        await document.fonts?.ready;
        if(hasPageOverflow(editorRootRef.current))throw new Error('PAGE_OVERFLOW');
        const pageEls = snapshotPages(editorRootRef.current.querySelectorAll(".page"));
        const previews = [];

        let idx = 0;
        for (const el of pageEls) {
          // Ensure it's visible and not scaled weirdly
          const canvas = await capturePage(el, {
            scale: 1.5,
            useCORS: true,
            logging: false,
          });

          const dataUrl = canvas.toDataURL("image/jpeg", 0.85);
          previews.push({
            index: idx,
            dataUrl,
            width: canvas.width,
            height: canvas.height,
            answerKey: Boolean(el.element.querySelector("[data-answer-key]")),
          });
          idx++;
        }

        setPages(previews);
        setSelected(previews.map((p) => p.index)); // select all by default
      } catch (e) {
        console.error(e);
        setError(e.message==='PAGE_OVERFLOW'?'Some content extends beyond a page. Reduce text size or working space before downloading.':'Failed to generate page previews.');
      } finally {
        setLoading(false);
      }
    }

    generatePreviews();
  }, [isOpen, editorRootRef]);

  const toggleSelected = (index) => {
    setSelected((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]
    );
  };

  const selectAll = () => {
    setSelected(pages.map((p) => p.index));
  };

  const clearAll = () => {
    setSelected([]);
  };

  const hasAnswers = pages.some(p => p.answerKey);
  const matchesSelection = subset => subset.length > 0 && selected.length === subset.length && subset.every(p => selected.includes(p.index));
  const activeSection = matchesSelection(pages) ? 'all' : matchesSelection(pages.filter(p => p.answerKey)) ? 'answers' : matchesSelection(pages.filter(p => !p.answerKey)) ? 'student' : null;

  const handleDownload = async () => {
    if (isDownloading) return;
  
    const selectedPages = pages.filter((p) =>
      selected.includes(p.index)
    );
  
    if (!selectedPages.length) return;
  
    setIsDownloading(true);
  
    try {
      // 1️⃣ Deduct credits FIRST (atomic & safe)
      const success = await deductCredits(
        user?.id,
        1,
        setWorksheetsCount
      );
  
      if (!success) {
        alert("You’ve run out of credits.");
        return;
      }
  
      // 2️⃣ Generate PDF only if deduction succeeded
      const pdf = new jsPDF("p", "mm", "a4");
      const PAGE_WIDTH = 210;
      const PAGE_HEIGHT = 297;
  
      selectedPages.forEach((page, idx) => {
        const img = page;
  
        // keep aspect ratio
        const ratio = img.width / img.height;
        let w = PAGE_WIDTH;
        let h = w / ratio;
  
        if (h > PAGE_HEIGHT) {
          h = PAGE_HEIGHT;
          w = h * ratio;
        }
  
        const x = (PAGE_WIDTH - w) / 2;
        const y = (PAGE_HEIGHT - h) / 2;
  
        if (idx > 0) {
          pdf.addPage();
        }
  
        pdf.addImage(img.dataUrl, "JPEG", x, y, w, h, undefined, "MEDIUM");
      });
  
      pdf.save("document.pdf");
      // Download succeeded, close with flag
      setIsDownloading(false);
      onClose(true);
    } catch (err) {
      console.error("Download failed:", err);
      alert("Something went wrong while generating the PDF.");
      setIsDownloading(false);
      onClose(false);
    } finally {
      setIsDownloading(false);
    }
  };
  
  
  if (!isOpen) return null;

  return (
    <div className="pdf-modal-backdrop">
      <div className="pdf-modal">
        <div className="pdf-modal-header">
          <h2>Preview & download</h2>
          <button onClick={() => onClose(false)} className="pdf-close-btn">
            ✕
          </button>
        </div>

        {!loading && pages.length > 0 && <div className="pdf-section-controls">
          <button aria-pressed={activeSection === "student" || (!hasAnswers && activeSection === "all")} onClick={() => setSelected(pages.filter(p => !p.answerKey).map(p => p.index))}>Student worksheet</button>
          {pages.some(p => p.answerKey) && <button aria-pressed={activeSection === "answers"} onClick={() => setSelected(pages.filter(p => p.answerKey).map(p => p.index))}>Answer key only</button>}
          {hasAnswers && <button aria-pressed={activeSection === "all"} onClick={selectAll}>Worksheet and answers</button>}
          <p className="pdf-info-note">Previewing is free. Downloading uses one credit.</p>
          <p className="pdf-info-note">Give AI answers a quick check, and keep student details out of prompts.</p>
        </div>}
        {loading && (
          <div className="pdf-modal-body">
            <p>Generating page previews…</p>
          </div>
        )}

        {!loading && error && (
          <div className="pdf-modal-body">
            <p style={{ color: "red" }}>{error}</p>
          </div>
        )}

        {!loading && !error && (
          <>
            <div className="pdf-modal-controls">
              <button onClick={selectAll}>Select all</button>
              <button onClick={clearAll}>Clear all</button>
              <span style={{ marginLeft: "auto", fontSize: "0.9rem" }}>
                {selected.length}/{pages.length} pages selected
              </span>
            </div>

            <div className="pdf-page-list">
              {pages.map((page) => (
                <div key={page.index} className="pdf-page-item">
                  <label className="pdf-page-label">
                    <input
                      type="checkbox"
                      checked={selected.includes(page.index)}
                      onChange={() => toggleSelected(page.index)}
                    />
                    Page {page.index + 1}
                  </label>
                  <div className="pdf-page-thumb-wrapper">
                    <img
                      src={page.dataUrl}
                      alt={`Page ${page.index + 1}`}
                      className="pdf-page-thumb"
                    />
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        <div className="pdf-modal-footer">
        {isAuthenticated && (
              //  <Link to="/buycredits">
            <div
              data-gtm-event="track-click"
              gtm-data-label="credits-click"
              className="credits">
              <img
                data-gtm-event="track-click"
                gtm-data-label="credits-click"
                src={dollar}
                alt="Credits"
                className="credits-icon-navbar"
              />
              <span
                data-gtm-event="track-click"
                gtm-data-label="credits-click"
                className="credits-text"> {worksheetsCount} Credits</span>
            </div>
            // </Link>
        )}



          <button onClick={() => onClose(false)}>Cancel</button>
          <button
            onClick={handleDownload}
            disabled={isDownloading || selected.length === 0}
            className="pdf-primary-btn"
          >
            Download PDF
          </button>
        </div>
      </div>
    </div>
  );
}
